"use client"

import * as React from "react"

import { useTranslations } from "@/i18n"
import { CreatePreviewCard, CreatePreviewFacts } from "@/features/create-overlay/components/create-overlay-layout"
import { useFormatPreviewDate } from "@/features/finance/components/create/finance-create-preview"
import { FundCard } from "@/features/finance/components/funds/fund-card-grid"
import { toMoneyOrNull } from "@/features/finance/lib/finance-utils"
import {
  FinanceFundKind,
  FinanceFundStatus,
  type FinanceCategoryScope,
  type FinanceFundRecord,
} from "@/features/finance/model/finance-types"

/** What the fund dialog knows so far, already resolved to names (the preview never looks anything up). */
export type FundPreviewDraft = {
  name: string
  description: string
  /** The money field's text as typed; only a valid amount for the currency becomes the card's goal. */
  targetAmount: string
  startDate: string
  endDate: string
  isRestricted: boolean
  /** The restriction period, only when its switch is on (`""` otherwise). */
  restrictionStartDate: string
  restrictionEndDate: string
  /** The activity the fund is dedicated to, or null. */
  dedicatedActivityName: string | null
  /** The activities that will draw from the fund (create only). */
  activityNames: string[]
}

const DRAFT_ID = "draft"

/**
 * The draft as the record the funds list draws. Creating: a custom, active fund at a zero balance. Editing: the
 * saved fund (its balance, kind and links) with the fields being changed laid over it.
 */
export function buildDraftFundRecord(
  draft: FundPreviewDraft,
  currency: string,
  scope: FinanceCategoryScope,
  record: FinanceFundRecord | null = null,
): FinanceFundRecord {
  const now = new Date(0).toISOString()
  const description = draft.description.trim()
  const linkedActivities = draft.activityNames.length + (draft.dedicatedActivityName ? 1 : 0)
  return {
    id: record?.id ?? DRAFT_ID,
    workspaceId: record?.workspaceId ?? "",
    branchId: record?.branchId ?? "",
    name: draft.name.trim(),
    nameTranslationKey: record?.nameTranslationKey ?? null,
    description: description.length > 0 ? description : null,
    status: record?.status ?? FinanceFundStatus.Active,
    kind: record?.kind ?? FinanceFundKind.Custom,
    linkedIncomeCategoryId: record?.linkedIncomeCategoryId ?? null,
    linkedIncomeAccountId: record?.linkedIncomeAccountId ?? null,
    scope: record?.scope ?? scope,
    targetAmount: toMoneyOrNull(draft.targetAmount, currency),
    startDate: draft.startDate || null,
    endDate: draft.endDate || null,
    isRestricted: draft.isRestricted,
    restrictionStartDate: draft.restrictionStartDate || null,
    restrictionEndDate: draft.restrictionEndDate || null,
    allowedExpenseAccountIds: record?.allowedExpenseAccountIds ?? [],
    currency: record?.currency ?? null,
    balance: record?.balance ?? "0",
    reserved: record?.reserved ?? "0",
    available: record?.available ?? "0",
    activityCount: record ? record.activityCount : linkedActivities,
    dedicatedActivityId: record?.dedicatedActivityId ?? null,
    dedicatedActivityName: draft.dedicatedActivityName,
    branch: record?.branch ?? { id: "", workspaceId: "", name: "", slug: "", status: "" },
    createdAt: record?.createdAt ?? now,
    updatedAt: record?.updatedAt ?? now,
  }
}

/** "Du 1 janvier 2027 au 30 juin 2027", "À partir du ...", "Jusqu'au ...", or null without either date. */
function usePeriodLabel() {
  const t = useTranslations("finance.funds.preview")
  const formatDate = useFormatPreviewDate()
  return React.useCallback(
    (start: string, end: string) => {
      const from = formatDate(start)
      const to = formatDate(end)
      if (from && to) return t("periodRange", { start: from, end: to })
      if (from) return t("periodFrom", { date: from })
      if (to) return t("periodUntil", { date: to })
      return null
    },
    [formatDate, t],
  )
}

/**
 * The fund dialog's live preview: the card exactly as the funds list will show it, then the facts the card does
 * not carry (period, dedicated activity, activities) and how its money may be spent, in plain words.
 */
export function FundCreatePreview({
  draft,
  currency,
  scope,
  spendingSummary,
  record = null,
}: {
  draft: FundPreviewDraft
  currency: string
  scope: FinanceCategoryScope
  /** The same plain-words sentence the form shows under its spending controls. */
  spendingSummary: string
  /** Editing: the saved fund, so the card keeps its real balance and links. */
  record?: FinanceFundRecord | null
}) {
  const t = useTranslations("finance.funds")
  const periodLabel = usePeriodLabel()
  const fund = buildDraftFundRecord(draft, currency, scope, record)

  const rows: { label: string; value: React.ReactNode }[] = [
    { label: t("preview.period"), value: periodLabel(draft.startDate, draft.endDate) },
    { label: t("fields.dedicatedActivity"), value: draft.dedicatedActivityName },
  ]
  if (!record && !draft.dedicatedActivityName) {
    rows.push({ label: t("fields.activities"), value: draft.activityNames.join(", ") })
  }

  return (
    <div className='flex flex-col gap-4' data-testid='fund-create-preview'>
      <ul>
        <FundCard fund={fund} currency={currency} label={fund.name || t("preview.namePlaceholder")} />
      </ul>
      <CreatePreviewCard>
        <CreatePreviewFacts rows={rows} />
      </CreatePreviewCard>
      <CreatePreviewCard className='flex flex-col gap-1' data-testid='fund-create-preview-restriction'>
        <span className='text-muted-foreground text-xs font-extrabold'>{t("spending.title")}</span>
        <span className='text-sm font-semibold break-words'>{spendingSummary}</span>
      </CreatePreviewCard>
      {record ? null : <p className='text-muted-foreground text-xs leading-snug'>{t("preview.note")}</p>}
    </div>
  )
}
